/**
 * Iter241 — Malzeme Kartları (Materials Hub).
 *
 * Central raw-material identity + regulatory-document workspace.
 * REUSES the existing Raw Material Card modal for detail view. Aggregates
 * the same materials + raw_material_cards + raw_material_documents rows —
 * NO new material database, NO duplicate identity, NO regulatory engine.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Btn, Badge, Input, Select, Table } from "@/components/UI";
import { Search, Sparkles, FileSearch, UploadCloud, Eye, Package, Info, Check, X, Plus, ChevronDown } from "lucide-react";
import RawMaterialCardModal from "@/components/RawMaterialCard";
import { usePagination, ListPagination } from "@/components/ListPagination";

const CONFIDENCE_TONE = {
  HIGH: "ok", MEDIUM: "attention", LOW: "missing",
};
const CONFIDENCE_LABEL = {
  HIGH: "Yüksek", MEDIUM: "Orta", LOW: "Düşük",
};

function TradeNamesPopover({ trade_names, count }) {
  const [open, setOpen] = useState(false);
  if (!count) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid="trade-names-chip"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100">
        {count} ticari isim <ChevronDown size={11}/>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-2 text-xs"
             data-testid="trade-names-popover">
          {trade_names.map((a, i) => (
            <div key={a.alias_id || i} className="py-1 border-b border-slate-100 last:border-0">
              <div className="font-semibold text-slate-900">{a.trade_name || "—"}</div>
              <div className="text-slate-500">
                {[a.supplier, a.manufacturer].filter(Boolean).join(" · ") || "Tedarikçi belirtilmemiş"}
              </div>
              {a.grade && <div className="text-slate-400 mt-0.5">Grade: {a.grade}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IdentityStatusBadge({ status }) {
  const map = { OK: ["ok", "Tam"], PARTIAL: ["attention", "Eksik"], MISSING: ["missing", "Yok"] };
  const [lvl, label] = map[status] || ["inactive", "—"];
  return <Badge level={lvl} data-testid={`identity-${status}`}>{label}</Badge>;
}
function DocStatusBadge({ status, count }) {
  const map = { OK: ["ok", `${count} belge`], PARTIAL: ["attention", `${count} belge`], MISSING: ["missing", "Yok"] };
  const [lvl, label] = map[status] || ["inactive", "—"];
  return <Badge level={lvl} data-testid={`doc-${status}`}>{label}</Badge>;
}

/* ────────────────────────────── Identity search dialog ─────────────────────────── */
function IdentitySearchDialog({ open, onClose, materialIds, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]); // [{material_id, candidates:[{...}]}]
  const [selected, setSelected] = useState({}); // {material_id: candidate_id}

  useEffect(() => {
    if (!open || !materialIds?.length) return;
    setBusy(true);
    api.post("/materials-hub/identity-search", { material_ids: materialIds })
      .then(r => setItems(r.items || []))
      .catch(e => alert(e.response?.data?.detail || "Arama başarısız"))
      .finally(() => setBusy(false));
  }, [open, materialIds]);

  const applyAll = async () => {
    const approvals = [];
    for (const it of items) {
      const sel = selected[it.material_id];
      const cand = (it.candidates || []).find(c => c.candidate_id === sel);
      if (!cand) continue;
      approvals.push({
        material_id: it.material_id,
        inci_name: cand.proposed_inci || null,
        cas_no: cand.proposed_cas || null,
        trade_name: cand.proposed_trade_name || null,
        supplier: cand.proposed_supplier || null,
        manufacturer: cand.proposed_manufacturer || null,
        source_url: cand.source_url || null,
        confidence: cand.match_confidence || null,
      });
    }
    if (!approvals.length) return alert("En az bir aday seçin");
    try {
      const r = await api.post("/materials-hub/identity-apply", { approvals });
      alert(`${r.applied} malzemeye kimlik uygulandı`);
      onApplied?.();
      onClose?.();
    } catch (e) { alert(e.response?.data?.detail || "Uygulama başarısız"); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center p-6 overflow-auto"
         data-testid="identity-search-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-lg font-bold">Toplu INCI / CAS / Ticari Ad Bul</div>
            <div className="text-xs text-slate-500">
              Aday listesi. Kullanıcı onayı olmadan hiçbir kart güncellenmez.
            </div>
          </div>
          <button onClick={onClose} data-testid="identity-close" className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
        </div>
        <div className="p-4 max-h-[65vh] overflow-y-auto">
          {busy && <div className="text-center py-8 text-slate-500">Adaylar aranıyor…</div>}
          {!busy && items.length === 0 && <div className="text-center py-8 text-slate-400">Aday yok.</div>}
          {items.map(it => (
            <div key={it.material_id} className="mb-4 border rounded-lg p-3"
                 data-testid={`ident-mat-${it.material_id}`}>
              <div className="font-semibold text-sm mb-2" data-testid={`ident-mat-label-${it.material_id}`}>
                Malzeme:{" "}
                {it.material_code || it.material_name ? (
                  <>
                    <span className="font-mono text-xs bg-slate-100 px-1 rounded">{it.material_code || "—"}</span>
                    <span className="ml-2">{it.material_name || ""}</span>
                  </>
                ) : (
                  <span className="font-mono text-xs bg-slate-100 px-1 rounded">{it.material_id.slice(0, 8)}</span>
                )}
                {it.search_terms?.name && (
                  <span className="ml-2 text-[10px] text-slate-500 font-normal">
                    (arama: <span className="font-mono">{it.search_terms.name}</span>)
                  </span>
                )}
              </div>
              {/* iter253 — AI-first identity search: real INCI/CAS/Ticari Ad with source citation */}
              {(it.candidates || []).length === 0
                ? <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid={`ident-empty-${it.material_id}`}>
                    {it.reason || "Kimlik doğrulanamadı — güvenilir kaynak bulunamadı."}
                  </div>
                : it.candidates.map(c => {
                    const chosen = selected[it.material_id] === c.candidate_id;
                    const unverified = c.verified === false;
                    return (
                      <label key={c.candidate_id}
                        className={`flex items-start gap-2 p-2 rounded cursor-pointer text-xs border mt-1 ${chosen ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`ident-cand-${c.candidate_id}`}>
                        <input type="radio" checked={chosen}
                          onChange={() => setSelected({ ...selected, [it.material_id]: c.candidate_id })}/>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge level={CONFIDENCE_TONE[c.match_confidence] || "inactive"}>
                              Güven: {CONFIDENCE_LABEL[c.match_confidence] || c.match_confidence || "—"}
                            </Badge>
                            {unverified && (
                              <span className="text-[10px] font-semibold text-red-800 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded">
                                DOĞRULANAMADI
                              </span>
                            )}
                            <span className="font-mono text-[10px] text-slate-500">{c.source_domain || c.source_name || "kaynak: —"}</span>
                          </div>
                          <div className="mt-1 space-y-0.5 text-slate-700">
                            {c.proposed_inci    ? <div>INCI: <b className="text-slate-900">{c.proposed_inci}</b></div>            : <div className="text-slate-400">INCI: —</div>}
                            {c.proposed_cas     ? <div>CAS: <b className="text-slate-900">{c.proposed_cas}</b></div>              : <div className="text-slate-400">CAS: —</div>}
                            {c.proposed_trade_name && <div>Ticari Ad / Alias: <b className="text-slate-900">{c.proposed_trade_name}</b></div>}
                            <div>Kaynak: <span className="text-slate-800">{c.source_name || c.source_domain || "—"}</span></div>
                            {c.reason && <div className="text-[10px] text-slate-500 italic mt-1">Gerekçe: {c.reason}</div>}
                          </div>
                          {c.source_url && (
                            <a href={c.source_url} target="_blank" rel="noreferrer"
                               className="text-indigo-600 hover:underline text-[10px] mt-1 inline-block">Kaynağı aç ↗</a>
                          )}
                        </div>
                      </label>
                    );
                  })}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={applyAll} data-testid="identity-apply-selected"
               disabled={Object.keys(selected).filter(k => selected[k]).length === 0}
               title={Object.keys(selected).filter(k => selected[k]).length === 0 ? "Önce en az bir aday seçin" : ""}>
            <Check size={12}/> Seçilenleri Uygula ({Object.keys(selected).filter(k => selected[k]).length})
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Document discovery dialog ─────────────────────── */
function DocumentDiscoveryDialog({ open, onClose, materialIds, onAttached }) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({}); // {candidate_id: {material_id, url, document_type}}
  const [docType, setDocType] = useState("SDS");

  // iter254: AI-first document discovery. Runs ONLY when the user explicitly
  // opens the dialog or presses "Ara" — no autoload on page mount.
  const runDiscovery = useCallback(() => {
    if (!materialIds?.length) return;
    setBusy(true);
    setSelected({});
    api.post("/materials-hub/document-discovery", { material_ids: materialIds, document_type: docType })
      .then(r => setItems(r.items || []))
      .catch(e => alert(e.response?.data?.detail || "Keşif başarısız"))
      .finally(() => setBusy(false));
  }, [materialIds, docType]);

  useEffect(() => { if (open) runDiscovery(); }, [open, runDiscovery]);

  const toggle = (mid, c) => {
    const next = { ...selected };
    if (next[c.candidate_id]) delete next[c.candidate_id];
    else next[c.candidate_id] = { material_id: mid, url: c.source_url, document_type: c.document_type };
    setSelected(next);
  };

  const attachSelected = async () => {
    const approvals = Object.values(selected);
    if (!approvals.length) return alert("En az bir belge seçin");
    try {
      const r = await api.post("/materials-hub/document-attach",
        { approvals: approvals.map(a => ({ ...a, confirm_large: true })) });
      const ok = (r.results || []).filter(x => x.ok).length;
      alert(`${ok} / ${approvals.length} belge içeri alındı`);
      onAttached?.();
      onClose?.();
    } catch (e) { alert(e.response?.data?.detail || "İçeri alma başarısız"); }
  };

  const selectedCount = Object.keys(selected).length;

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center p-6 overflow-auto"
         data-testid="doc-discovery-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-lg font-bold">Toplu Belge Bul — AI Araştırması</div>
            <div className="text-xs text-slate-500">
              AI otoritatif kaynaklardan aday belgeler getirir. Seçilenler mevcut güvenli indirme akışıyla içeri alınır — hiçbir belge otomatik indirilmez.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={docType} onChange={e => setDocType(e.target.value)} label="Belge Tipi"
              data-testid="doc-discovery-type">
              <option value="SDS">SDS</option>
              <option value="TDS">TDS</option>
              <option value="COA">COA</option>
              <option value="IFRA">IFRA</option>
              <option value="ALLERGEN">Allergen</option>
              <option value="SPECIFICATION">Specification</option>
              <option value="CERTIFICATE">Certificate</option>
            </Select>
            <Btn variant="secondary" onClick={runDiscovery} data-testid="doc-discovery-run"><Search size={12}/> Ara</Btn>
            <button onClick={onClose} data-testid="doc-discovery-close" className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
          </div>
        </div>
        <div className="p-4 max-h-[65vh] overflow-y-auto">
          {busy && <div className="text-center py-8 text-slate-500">AI adayları araştırıyor…</div>}
          {!busy && items.length === 0 && <div className="text-center py-8 text-slate-400">Aday yok.</div>}
          {items.map(it => (
            <div key={it.material_id} className="mb-4 border rounded-lg p-3"
                 data-testid={`disc-mat-${it.material_id}`}>
              <div className="font-semibold text-sm mb-2" data-testid={`disc-mat-label-${it.material_id}`}>
                {it.material_code && (
                  <span className="font-mono text-xs bg-slate-100 px-1 rounded mr-2">{it.material_code}</span>
                )}
                {it.material_name}
              </div>
              {(it.candidates || []).length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                     data-testid={`disc-empty-${it.material_id}`}>
                  {it.reason || "Doğrulanmış SDS bulunamadı."}
                </div>
              ) : it.candidates.map(c => {
                const chosen = !!selected[c.candidate_id];
                const idnFail = c.identity_ok === false;
                return (
                  <label key={c.candidate_id}
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer text-xs border mt-1 ${chosen ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`disc-cand-${c.candidate_id}`}>
                    <input type="checkbox" checked={chosen} onChange={() => toggle(it.material_id, c)}/>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge level={CONFIDENCE_TONE[c.match_confidence] || "inactive"}>
                          Güven: {CONFIDENCE_LABEL[c.match_confidence] || c.match_confidence || "—"}
                        </Badge>
                        {idnFail && (
                          <span className="text-[10px] font-semibold text-red-800 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded">
                            Kimlik eşleşmesi doğrulanamadı
                          </span>
                        )}
                        {c.duplicate && <span className="text-[10px] text-amber-700 bg-amber-100 px-1 rounded">Mevcut SDS var</span>}
                        <span className="font-mono text-[10px] text-slate-500">{c.source_domain || "—"}</span>
                      </div>
                      <div className="mt-1 text-slate-800 font-medium">{c.title || c.source_url}</div>
                      <div className="mt-0.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-700">
                        <div>Üretici: <span className="text-slate-900">{c.manufacturer_name || "—"}</span></div>
                        <div>Tedarikçi: <span className="text-slate-900">{c.supplier_name || "—"}</span></div>
                        <div>CAS: <span className="text-slate-900">{c.cas_no || "—"}</span></div>
                        <div>INCI: <span className="text-slate-900">{c.inci_name || "—"}</span></div>
                        <div>Ticari Ad: <span className="text-slate-900">{c.material_trade_name || "—"}</span></div>
                        <div>Revizyon: <span className="text-slate-900">{c.revision_date || "—"}{c.language ? ` · ${c.language}` : ""}</span></div>
                        <div className="col-span-2">Kaynak: <span className="text-slate-900">{c.source_name || c.source_domain || "—"}</span></div>
                      </div>
                      {c.duplicate && c.latest_existing_revision && (
                        <div className="mt-1 text-[10px] text-amber-800">Mevcut kayıt: {c.latest_existing_revision} — Yeni revizyon eklemeden önce karşılaştırın.</div>
                      )}
                      {c.match_reason && <div className="text-[10px] text-slate-500 italic mt-1">Gerekçe: {c.match_reason}</div>}
                      <a href={c.source_url} target="_blank" rel="noreferrer"
                         className="text-indigo-600 hover:underline text-[10px] mt-1 inline-block">Önizle Kaynak ↗</a>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={attachSelected} data-testid="doc-discovery-attach"
               disabled={selectedCount === 0}
               title={selectedCount === 0 ? "Önce en az bir belge seçin" : ""}>
            <Check size={12}/> Seçilenleri İçe Aktar ({selectedCount})
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Bulk local upload dialog ───────────────────────── */
function BulkUploadDialog({ open, onClose, onAttached }) {
  const [files, setFiles] = useState([]);
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null); // {files:[{...}]}
  const [remap, setRemap] = useState({}); // {file_id: material_id}
  const [remapType, setRemapType] = useState({}); // {file_id: doc_type}
  const [matSearch, setMatSearch] = useState("");
  const [mats, setMats] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/materials-hub/list", { q: matSearch }).then(r => setMats(r.rows || []));
  }, [open, matSearch]);

  const inspect = async () => {
    if (!files.length) return alert("Dosya seçin");
    setInspecting(true);
    setInspection(null);
    setRemap({}); setRemapType({});
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/materials-hub/bulk-upload-inspect`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const j = await r.json();
      setInspection(j);
      const dm = {}; const dt = {};
      (j.files || []).forEach(f => {
        dm[f.file_id] = f.guessed_material_id || "";
        dt[f.file_id] = f.guessed_document_type || "OTHER";
      });
      setRemap(dm); setRemapType(dt);
    } catch (e) { alert(e.message); }
    finally { setInspecting(false); }
  };

  const attach = async () => {
    if (!inspection) return;
    const orderedFiles = [];
    const mids = [];
    const dtypes = [];
    for (const meta of inspection.files) {
      const mid = remap[meta.file_id];
      if (!mid) continue;
      const orig = files.find(x => x.name === meta.filename);
      if (!orig) continue;
      orderedFiles.push(orig);
      mids.push(mid);
      dtypes.push(remapType[meta.file_id] || "OTHER");
    }
    if (!orderedFiles.length) return alert("Onaylı eşleşme yok");
    setBusy(true);
    try {
      const fd = new FormData();
      orderedFiles.forEach(f => fd.append("files", f));
      fd.append("material_ids", mids.join(","));
      fd.append("document_types", dtypes.join(","));
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/materials-hub/bulk-upload-attach`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const j = await r.json();
      const ok = (j.results || []).filter(x => x.ok).length;
      alert(`${ok} / ${orderedFiles.length} dosya kaydedildi`);
      onAttached?.();
      onClose?.();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center p-6 overflow-auto"
         data-testid="bulk-upload-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-lg font-bold">Toplu Belge Yükle — Akıllı Eşleştirme</div>
            <div className="text-xs text-slate-500">
              Deterministik önce, sonra AI. Onay olmadan hiçbir belge karta bağlanmaz.
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-upload-close" className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
        </div>
        <div className="p-4">
          {!inspection && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">Dosyalar (birden çok)</span>
                <input type="file" multiple onChange={e => setFiles(Array.from(e.target.files || []))}
                       className="text-xs block mt-1" data-testid="bulk-upload-files"/>
                {files.length > 0 && <div className="text-xs text-slate-600 mt-1">{files.length} dosya seçildi</div>}
              </label>
              <Btn onClick={inspect} disabled={inspecting || !files.length} data-testid="bulk-upload-inspect">
                <Sparkles size={12}/> {inspecting ? "Analiz ediliyor…" : "Akıllı Eşleştir"}
              </Btn>
            </div>
          )}
          {inspection && (
            <div>
              <Input label="Malzeme ara" value={matSearch} onChange={e => setMatSearch(e.target.value)}
                     placeholder="ad / kod" data-testid="bulk-upload-mat-search"/>
              <div className="mt-3 max-h-[55vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-2 py-1">Dosya</th>
                      <th className="text-left px-2 py-1">Güven</th>
                      <th className="text-left px-2 py-1">Belge Tipi</th>
                      <th className="text-left px-2 py-1">Hedef Malzeme (değiştirilebilir)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspection.files.map(f => (
                      <tr key={f.file_id} className="border-t" data-testid={`bulk-row-${f.file_id}`}>
                        <td className="px-2 py-1">
                          <div className="font-mono text-[11px]">{f.filename}</div>
                          {f.remap_required && <div className="text-[10px] text-amber-700">Eşleşme gerekli</div>}
                        </td>
                        <td className="px-2 py-1">
                          <Badge level={CONFIDENCE_TONE[f.confidence] || "inactive"}>{CONFIDENCE_LABEL[f.confidence]}</Badge>
                        </td>
                        <td className="px-2 py-1">
                          <select value={remapType[f.file_id] || "OTHER"}
                            onChange={e => setRemapType({ ...remapType, [f.file_id]: e.target.value })}
                            data-testid={`bulk-type-${f.file_id}`}
                            className="text-xs border rounded px-1 py-0.5">
                            {["SDS","TDS","COA","IFRA","ALLERGEN","SPECIFICATION","CERTIFICATE","SAMPLE_COA","OTHER"].map(t =>
                              <option key={t}>{t}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <select value={remap[f.file_id] || ""}
                            onChange={e => setRemap({ ...remap, [f.file_id]: e.target.value })}
                            data-testid={`bulk-mat-${f.file_id}`}
                            className="text-xs border rounded px-1 py-0.5 w-full">
                            <option value="">— Eşleşme yok (atla) —</option>
                            {mats.map(m =>
                              <option key={m.material_id} value={m.material_id}>{m.code} · {m.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          {inspection && (
            <Btn onClick={attach} disabled={busy} data-testid="bulk-upload-attach">
              <Check size={12}/> {busy ? "Yükleniyor…" : "Onaylı Eşleşmeleri Kaydet"}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Main page ─────────────────────────────────────── */
// `embedded` prop: when rendered inside another page's tab (e.g. Envanter →
// Malzeme Kartları), skip the top PageHeader + intro banner so the host
// page's header stays authoritative. Standalone /materials-hub route still
// works untouched.
export default function MaterialsHubPage({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ q: "", supplier: "", missing_inci: false, missing_cas: false, missing_docs: false });
  const [sel, setSel] = useState({}); // {material_id:true}
  const [cardMid, setCardMid] = useState(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const p = {};
      if (f.q) p.q = f.q;
      if (f.supplier) p.supplier = f.supplier;
      if (f.missing_inci) p.missing_inci = true;
      if (f.missing_cas) p.missing_cas = true;
      if (f.missing_docs) p.missing_docs = true;
      const r = await api.get("/materials-hub/list", p);
      setRows(r.rows || []);
    } catch (e) { alert(e.response?.data?.detail || "Yükleme hatası"); }
    finally { setBusy(false); }
  }, [f]);
  useEffect(() => { load(); }, [f.missing_inci, f.missing_cas, f.missing_docs]);

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.code + " " + r.name + " " + (r.category || "") + " " +
       (r.inci_first || "") + " " + (r.cas_first || "") + " " +
       (r.primary_supplier || "") + " " + (r.trade_name_first || "")
      ).toLowerCase().includes(q)
    );
  }, [rows, f.q]);

  const selectedIds = useMemo(() => Object.keys(sel).filter(k => sel[k]), [sel]);
  const toggleAll = () => {
    const on = selectedIds.length !== filtered.length;
    const next = {};
    if (on) filtered.forEach(r => { next[r.material_id] = true; });
    setSel(next);
  };
  const targetIds = () => selectedIds.length ? selectedIds : filtered.map(r => r.material_id);
  const pg = usePagination(filtered, "materials-hub", 100);

  return (
    <div>
      {!embedded && (
        <>
          <PageHeader
            title="Malzeme Kartları"
            description="Merkezi hammadde kimlik + regülasyon belge merkezi — mevcut Hammadde Kartı altyapısıyla."/>

          <Card className="p-3 mb-3" data-testid="mh-info-banner">
            <div className="text-xs text-slate-600 flex items-start gap-2">
              <Info size={13} className="mt-0.5 text-indigo-600 shrink-0"/>
              <div>
                <span className="font-semibold text-indigo-700">1 malzeme = 1 kart.</span>
                {" "}Aynı canonical materyal farklı tedarikçilerde farklı ticari adlarla geçebilir. Çoklu ticari adları
                ticari-isim aliasları olarak <b>aynı kartta</b> saklıyoruz — asla yeni malzeme kaydı açılmaz.
                AI/internet önerileri <b>kullanıcı onayı</b> olmadan kaydedilmez.
              </div>
            </div>
          </Card>
        </>
      )}

      <Card className="p-3 mb-3" data-testid="mh-filters">
        <div className="flex flex-wrap items-end gap-2">
          <Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })}
                 placeholder="kod / ad / INCI / CAS / ticari ad" data-testid="mh-search"/>
          <Input label="Tedarikçi" value={f.supplier} onChange={e => setF({ ...f, supplier: e.target.value })}
                 placeholder="tedarikçi/üretici" data-testid="mh-supplier"/>
          <label className="text-xs flex items-center gap-1 pt-4">
            <input type="checkbox" checked={f.missing_inci} onChange={e => setF({ ...f, missing_inci: e.target.checked })} data-testid="mh-miss-inci"/> INCI eksik
          </label>
          <label className="text-xs flex items-center gap-1 pt-4">
            <input type="checkbox" checked={f.missing_cas} onChange={e => setF({ ...f, missing_cas: e.target.checked })} data-testid="mh-miss-cas"/> CAS eksik
          </label>
          <label className="text-xs flex items-center gap-1 pt-4">
            <input type="checkbox" checked={f.missing_docs} onChange={e => setF({ ...f, missing_docs: e.target.checked })} data-testid="mh-miss-docs"/> Belge yok
          </label>
          <div className="flex-1"/>
          <div className="text-xs text-slate-500 pt-4" data-testid="mh-count">
            {filtered.length} malzeme {selectedIds.length ? `· ${selectedIds.length} seçili` : ""}
          </div>
        </div>
      </Card>

      <Card className="p-3 mb-3 bg-indigo-50/30 border-indigo-100" data-testid="mh-actions">
        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => setIdentityOpen(true)} disabled={!targetIds().length}
               data-testid="mh-action-identity">
            <Sparkles size={12}/> Toplu INCI / CAS / Ticari Ad Bul
            <span className="text-[10px] ml-1">({targetIds().length})</span>
          </Btn>
          <Btn onClick={() => setDiscOpen(true)} disabled={!targetIds().length}
               data-testid="mh-action-doc">
            <FileSearch size={12}/> Toplu Belge Bul (İnternet)
            <span className="text-[10px] ml-1">({targetIds().length})</span>
          </Btn>
          <Btn onClick={() => setBulkOpen(true)} data-testid="mh-action-bulk-upload">
            <UploadCloud size={12}/> Toplu Belge Yükle
          </Btn>
          <div className="flex-1"/>
          <Btn variant="secondary" onClick={load} data-testid="mh-refresh">Yenile</Btn>
        </div>
      </Card>

      <Table testid="mh-table" rows={pg.paged} columns={[
        { label: (
            <input type="checkbox"
              checked={filtered.length > 0 && selectedIds.length === filtered.length}
              onChange={toggleAll} data-testid="mh-sel-all"/>
          ),
          render: r => (
            <input type="checkbox" checked={!!sel[r.material_id]}
              onChange={e => setSel({ ...sel, [r.material_id]: e.target.checked })}
              data-testid={`mh-sel-${r.material_id}`}/>
          )},
        { label: "Kod", render: r => <span className="font-mono text-xs font-semibold text-indigo-700">{r.code || "—"}</span> },
        { label: "Ad", render: r => <span className="text-sm">{r.name}</span> },
        { label: "Kategori", render: r => <span className="text-xs text-slate-600">{r.category || "—"}</span> },
        { label: "INCI", render: r => <span className="text-xs font-mono">{r.inci_first || <span className="text-slate-300">—</span>}</span> },
        { label: "CAS", render: r => <span className="text-xs font-mono">{r.cas_first || <span className="text-slate-300">—</span>}</span> },
        { label: "Ticari İsim", render: r => (
            r.trade_name_count > 1
              ? <TradeNamesPopover trade_names={r.trade_names} count={r.trade_name_count}/>
              : <span className="text-xs text-slate-700">{r.trade_name_first || "—"}</span>
          )},
        { label: "Tedarikçi", render: r => <span className="text-xs text-slate-600">{r.primary_supplier || "—"}</span> },
        { label: "Kimlik", render: r => <IdentityStatusBadge status={r.identity_status}/> },
        { label: "Belge", render: r => <DocStatusBadge status={r.document_status} count={r.document_count}/> },
        { label: "İşlem", render: r => (
            <Btn size="sm" variant="secondary" onClick={() => setCardMid(r.material_id)}
              data-testid={`mh-open-${r.material_id}`}>
              <Eye size={12}/> Aç
            </Btn>
          )},
      ]}/>
      <ListPagination pagination={pg} testid="mh-pagination"/>
      {!busy && filtered.length === 0 && (
        <div className="text-center py-6 text-slate-400 text-sm">Filtreye uygun malzeme yok.</div>
      )}

      <RawMaterialCardModal
        open={!!cardMid}
        materialId={cardMid}
        onClose={() => { setCardMid(null); load(); }}/>

      <IdentitySearchDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        materialIds={targetIds()}
        onApplied={load}/>

      <DocumentDiscoveryDialog
        open={discOpen}
        onClose={() => setDiscOpen(false)}
        materialIds={targetIds()}
        onAttached={load}/>

      <BulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onAttached={load}/>
    </div>
  );
}
